// src/components/TemplateEditor.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface TemplateEditorProps {
  templateId?: string;
  accountId?: string;
  onSave: () => void;
  onCancel: () => void;
}

export function TemplateEditor({ templateId, accountId, onSave, onCancel }: TemplateEditorProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    trigger_type: 'first_message',
    trigger_keywords: '',
    template_content: '',
    use_ai_enhancement: true,
    ai_system_prompt: '',
  });

  const loadTemplate = useCallback(async () => {
    try {
      const templates = await api.getTemplates();
      const template = templates.find((t) => t.id === templateId);
      if (template) {
        setFormData({
          name: template.name,
          trigger_type: template.trigger_type || 'first_message',
          trigger_keywords: template.trigger_keywords || '',
          template_content: template.template_content,
          use_ai_enhancement: template.use_ai_enhancement,
          ai_system_prompt: template.ai_system_prompt || '',
        });
      }
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  }, [templateId]);

  useEffect(() => {
    if (templateId) {
      loadTemplate();
    }
  }, [templateId, loadTemplate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const templateData = {
        ...formData,
        instagram_account_id: accountId || undefined,
        trigger_keywords: formData.trigger_keywords || null,
      };

      if (templateId) {
        await api.updateTemplate(templateId, templateData);
      } else {
        await api.createTemplate(templateData);
      }

      onSave();
    } catch (error: any) {
      alert(error.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Template Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border rounded-md"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Trigger Type</label>
        <select
          value={formData.trigger_type}
          onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
          className="w-full px-3 py-2 border rounded-md"
        >
          <option value="first_message">First Message</option>
          <option value="keyword">Keyword</option>
        </select>
      </div>

      {formData.trigger_type === 'keyword' && (
        <div>
          <label className="block text-sm font-medium mb-1">Keywords (comma-separated)</label>
          <input
            type="text"
            value={formData.trigger_keywords}
            onChange={(e) => setFormData({ ...formData, trigger_keywords: e.target.value })}
            className="w-full px-3 py-2 border rounded-md"
            placeholder="help, support, question"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1">Template Content</label>
        <textarea
          value={formData.template_content}
          onChange={(e) => setFormData({ ...formData, template_content: e.target.value })}
          className="w-full px-3 py-2 border rounded-md"
          rows={4}
          required
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="use_ai"
          checked={formData.use_ai_enhancement}
          onChange={(e) => setFormData({ ...formData, use_ai_enhancement: e.target.checked })}
          className="mr-2"
        />
        <label htmlFor="use_ai" className="text-sm">Use AI Enhancement (Gemini)</label>
      </div>

      {formData.use_ai_enhancement && (
        <div>
          <label className="block text-sm font-medium mb-1">AI System Prompt (optional)</label>
          <textarea
            value={formData.ai_system_prompt}
            onChange={(e) => setFormData({ ...formData, ai_system_prompt: e.target.value })}
            className="w-full px-3 py-2 border rounded-md"
            rows={3}
            placeholder="You are a helpful customer service assistant..."
          />
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Template'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
